import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { axiosApi } from "./axios";
import {
  CreateInvoiceDto,
  GetInvoiceQueryDto,
  GetInvoiceResponseDto,
  GetInvoiceStatsExcelDto,
  GetInvoiceStatsResponseDto,
  UpdateInvoiceDto,
} from "@/types/api/api-types";

//#region V1
const getInvoiceV1 = async (payload: GetInvoiceQueryDto) => {
  return axiosApi
    .get<GetInvoiceResponseDto>("v1/invoice", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetInvoiceV1 = (payload: GetInvoiceQueryDto) => {
  return useQuery({
    queryKey: ["invoice", payload],
    queryFn: () => getInvoiceV1(payload),
  });
};

const getInvoiceBoV1 = async (payload: GetInvoiceQueryDto) => {
  return axiosApi
    .get<GetInvoiceResponseDto>("v1/invoice/bo", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetInvoiceBoV1 = (payload: GetInvoiceQueryDto) => {
  return useQuery({
    queryKey: ["invoice", payload],
    queryFn: () => getInvoiceBoV1(payload),
  });
};

const getLitigeV1 = async (payload: GetInvoiceQueryDto) => {
  return axiosApi
    .get<GetInvoiceResponseDto>("v1/litige", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetLitigeV1 = (payload: GetInvoiceQueryDto) => {
  return useQuery({
    queryKey: ["litige", payload],
    queryFn: () => getLitigeV1(payload),
  });
};

const getLitigeBoV1 = async (payload: GetInvoiceQueryDto) => {
  return axiosApi
    .get<GetInvoiceResponseDto>("v1/litige/bo", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetLitigeBoV1 = (payload: GetInvoiceQueryDto) => {
  return useQuery({
    queryKey: ["litige", payload],
    queryFn: () => getLitigeBoV1(payload),
  });
};

const getLitigeCountV1 = async (payload: GetInvoiceQueryDto) => {
  return axiosApi
    .get<number>("v1/litige/count", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetLitigeCountV1 = (payload: GetInvoiceQueryDto) => {
  return useQuery({
    queryKey: ["litige", "count", payload],
    queryFn: () => getLitigeCountV1(payload),
  });
};

const getInvoiceCountV1 = async (payload: GetInvoiceQueryDto) => {
  return axiosApi
    .get<number>("v1/invoice/count", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetInvoiceCountV1 = (payload: GetInvoiceQueryDto) => {
  return useQuery({
    queryKey: ["invoice", "count", payload],
    queryFn: () => getInvoiceCountV1(payload),
  });
};
//#endregion

const getInvoicesStats = async (payload: GetInvoiceQueryDto) => {
  return axiosApi
    .get<GetInvoiceStatsResponseDto>("/invoice/stats", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetInvoicesStats = (payload: GetInvoiceQueryDto) => {
  return useQuery({
    queryKey: ["invoiceStats", payload],
    queryFn: () => getInvoicesStats(payload),
  });
};

const getInvoicesStatsExcel = async (payload: GetInvoiceQueryDto) => {
  return axiosApi
    .get<GetInvoiceStatsExcelDto>("/invoice/stats/excel", {
      params: payload,
    })
    .then((res) => res.data);
};

export const useGetInvoicesStatsExcel = (payload: GetInvoiceQueryDto) => {
  return useQuery({
    queryKey: ["invoiceStatsExcel", payload],
    queryFn: () => getInvoicesStatsExcel(payload),
  });
};

const createInvoice = async (data: CreateInvoiceDto) => {
  return axiosApi.post(`/invoice`, data);
};

export const useCreateInvoice = () => {
  const { t } = useTranslation("boInvoices");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["invoice"],
      });
      queryClient.invalidateQueries({
        queryKey: ["litige"],
      });
    },
    onError: () => {
      toast.error(t("invoiceApi.createError"));
    },
  });
};

const createInvoiceBo = async (data: CreateInvoiceDto) => {
  return axiosApi.post(`/invoice/bo`, data);
};

export const useCreateInvoiceBo = () => {
  const { t } = useTranslation("boInvoices");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createInvoiceBo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["invoice"],
      });
      queryClient.invalidateQueries({
        queryKey: ["litige"],
      });
    },
    onError: () => {
      toast.error(t("invoiceApi.createError"));
    },
  });
};

const updateInvoice = async ({ id, data }: { id: string; data: UpdateInvoiceDto }) => {
  return axiosApi.patch(`/invoice/${id}`, data);
};

export const useUpdateInvoice = () => {
  const { t } = useTranslation("boInvoices");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["litige"] });
      queryClient.invalidateQueries({ queryKey: ["invoice"] });
      toast.success(t("invoiceApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("invoiceApi.updateError"));
    },
  });
};

const batchValidateInvoice = async () => {
  return axiosApi.patch(`/invoice/batch-validate`);
};

export const useBatchValidateInvoice = () => {
  const { t } = useTranslation("boInvoices");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: batchValidateInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["litige"] });
      queryClient.invalidateQueries({ queryKey: ["invoice"] });
    },
    onError: () => {
      toast.error(t("invoiceApi.updateError"));
    },
  });
};

const deleteInvoice = async (id: string) => {
  return axiosApi.delete(`/invoice/${id}`);
};

export const useDeleteInvoice = () => {
  const { t } = useTranslation("boInvoices");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice"] });
      queryClient.invalidateQueries({ queryKey: ["litige"] });
    },
    onError: () => {
      toast.error(t("invoiceApi.deleteError"));
    },
  });
};

const deleteInvoiceBo = async (id: string) => {
  return axiosApi.delete(`/invoice/bo/${id}`);
};

export const useDeleteInvoiceBo = () => {
  const { t } = useTranslation("boInvoices");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteInvoiceBo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice"] });
      queryClient.invalidateQueries({ queryKey: ["litige"] });
    },
    onError: () => {
      toast.error(t("invoiceApi.deleteError"));
    },
  });
};

const unblockInvoice = async (id: string) => {
  return axiosApi.patch(`/v1/invoice/unblock/${id}`);
};

export const useUnblockInvoice = () => {
  const { t } = useTranslation("boInvoices");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unblockInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice"] });
      queryClient.invalidateQueries({ queryKey: ["litige"] });
    },
    onError: () => {
      toast.error(t("invoiceApi.unblockError"));
    },
  });
};
